import { SUPPORTED_LANGUAGES, formatLanguage } from 'constants/languages';

export interface PrintingsFilterState {
  foil: string[];
  /** Frontend uppercase codes ('EN', 'ZHS') — never display names or backend codes. */
  languages: string[];
  conditions: string[];
}

/** Canonical finish order (non-foil first). Etched is a want intent flag since Track D (acceptEtched). */
export const FOIL_OPTIONS = ['Non-foil', 'Foil', 'Etched'];
export const CONDITION_OPTIONS = ['NM', 'LP', 'MP', 'HP'];
/**
 * Chips display the full name but toggle the CODE. The state's language values
 * arrive from the page as codes (saved want-list defaults are 'EN', edit-mode
 * entries are backend 'en'), so a chip comparing display names never matched —
 * clicking English appended 'English' to twelve codes, the dimension diverged
 * from its load default, and the token row lit up with every language at once.
 */
export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((l) => ({
  value: l.code,
  label: l.displayName,
}));

/** How many language chips show before the "+N more" expander. */
export const LANGUAGE_COLLAPSE_AT = 6;

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

/**
 * The Finish field in the filter row (single-select). 'Custom' is not a choice
 * the user can click: it is shown, selected, only while the state holds a
 * multi-finish subset that is not every available finish -- e.g. a saved want
 * that accepts foil + etched. Showing 'All' there would lie, because the grid
 * would still be filtered.
 */
export type FinishChip = 'All' | 'Foil' | 'Non-foil' | 'Etched' | 'Custom';

/** Finish labels a printing of this card exists in, in canonical order. */
export const availableFinishOptions = (disabledFinishes: string[] = []): string[] =>
  FOIL_OPTIONS.filter((opt) => !disabledFinishes.includes(opt));

/**
 * Which chip the current foil state reads as. Only finishes the card exists in
 * count: a stored flag for a finish no printing has is inert (D-3).
 */
export const finishChipFor = (foil: string[], available: string[]): FinishChip => {
  const on = available.filter((opt) => foil.includes(opt));
  if (available.length > 0 && on.length === available.length) return 'All';
  if (on.length === 1) return on[0] as FinishChip;
  return 'Custom';
};

/** The foil state a chip click writes. 'All' accepts every available finish. */
export const foilForChip = (chip: Exclude<FinishChip, 'Custom'>, available: string[]): string[] =>
  chip === 'All' ? [...available] : [chip];

/**
 * The save button names the finish being saved whenever it is not All, because
 * the Finish chips are also the want's finish acceptance: a trader who taps
 * Foil to browse must see "Foil only" on the button before saving.
 */
export const withFinishSuffix = (base: string, foil: string[], available: string[]): string => {
  const chip = finishChipFor(foil, available);
  if (chip === 'All') return base;
  if (chip !== 'Custom') return `${base} · ${chip} only`;
  const on = available.filter((opt) => foil.includes(opt));
  return `${base} · ${on.length > 0 ? on.join(' + ') : 'No finish'}`;
};

/**
 * The badge counts APPLIED values -- those differing from what the page loaded
 * with. In edit mode the load-time values are the user's saved want-list
 * preferences, so "no badge" correctly means "unchanged from what you saved",
 * not "no filters at all".
 *
 * Finish is NOT counted: it has its own field in the filter row that shows its
 * state, so counting it here would report the same choice twice.
 */
export const countAppliedFilters = (
  current: PrintingsFilterState,
  defaults: PrintingsFilterState,
): number => {
  let n = 0;
  if (!sameSet(current.languages, defaults.languages)) n += 1;
  if (!sameSet(current.conditions, defaults.conditions)) n += 1;
  return n;
};

export interface PrintingsToken {
  id: string;
  label: string;
  dimension: keyof PrintingsFilterState;
  value: string;
}

/**
 * One token per applied VALUE, so the x removes exactly that value.
 *
 * A dimension only produces tokens once it differs from the load-time default;
 * otherwise every page would open showing a dozen "English", "NM" tokens that
 * the user never chose.
 */
export const buildTokens = (
  current: PrintingsFilterState,
  defaults: PrintingsFilterState,
): PrintingsToken[] => {
  const tokens: PrintingsToken[] = [];
  const push = (dimension: keyof PrintingsFilterState, values: string[]) => {
    values.forEach((value) =>
      tokens.push({
        id: `${dimension}:${value}`,
        // Language values are codes; show the badge abbreviation ('EN', 'JP').
        label: dimension === 'languages' ? formatLanguage(value) : value,
        dimension,
        value,
      }),
    );
  };

  // No finish tokens: the Finish field in the row shows its own state.
  if (!sameSet(current.languages, defaults.languages)) push('languages', current.languages);
  if (!sameSet(current.conditions, defaults.conditions)) push('conditions', current.conditions);

  return tokens;
};
